import { forwardRef } from 'react';
import SearchSelect from './SearchSelect';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label?: string;
  error?: string;
  options: SelectOption[];
  fullWidth?: boolean;
  className?: string;
  placeholder?: string;
  /** If true, renders a searchable dropdown instead of native <select>. */
  searchable?: boolean;
  /** Used by searchable mode. If omitted, we will call `onChange` with a synthetic event. */
  onValueChange?: (value: string) => void;
  /** Placeholder for the search input (searchable mode). */
  searchPlaceholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    error,
    options,
    fullWidth = true,
    className = '',
    id,
    placeholder = '—',
    searchable = false,
    onValueChange,
    searchPlaceholder,
    ...props
  },
  ref
) {
  const selectId = id ?? label?.toLowerCase().replace(/\s/g, '-');

  if (searchable) {
    const emitBlur = () => {
      if (!props.onBlur) return;
      // Formik's handleBlur expects a SyntheticEvent with persist().
      const e = {
        persist: () => {},
        target: {
          id: selectId,
          name: (props.name ?? selectId) as string,
          value: String((props.value ?? '') as string),
        },
        currentTarget: {
          id: selectId,
          name: (props.name ?? selectId) as string,
          value: String((props.value ?? '') as string),
        },
        type: 'blur',
      };
      (props.onBlur as unknown as (evt: unknown) => void)(e);
    };

    return (
      <SearchSelect
        id={selectId}
        label={label}
        options={options}
        value={String((props.value ?? '') as string)}
        disabled={Boolean(props.disabled)}
        error={error}
        fullWidth={fullWidth}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        onChange={(v) => {
          onValueChange?.(v);
          if (props.onChange) {
            // Back-compat: allow handlers written for <select onChange>.
            (props.onChange as unknown as (e: { target: { value: string } }) => void)({ target: { value: v } });
          }
        }}
        onBlur={() => {
          emitBlur();
        }}
      />
    );
  }

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-slate-700">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={selectId}
        className={`pro-input ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''} ${fullWidth ? 'w-full' : ''} ${className}`}
        {...props}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
});

export default Select;
